import { createSafeGlobalState } from '@/helpers/createSafeGlobalState';
import { SnowplowContext } from '@/helpers/tracking/providers';

interface SessionTrackerOptions<T> {
	id: symbol;

	/** Set true to include its context in all events while the session is active. */
	influenced: boolean;

	/** Configure auto session timeout. */
	options?: {
		/** How often in ms to check if the session timed out. Default: 5 minutes. */
		checkIntervalMs?: number;

		/** Duration of a session in ms. Default: 30 minutes. */
		sessionTimeoutMs?: number;

		/** Callback that runs when auto ending a session. */
		onSessionTimeout?: (previousSession: T | null) => void;
	};
}

// 5 minutes
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

// 30 minutes
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

export interface Session<T = any, S = any> {
	/** Arbitrary data to attach to the session. */
	data: T;

	/**
	 * Page name, url, or other starting point of the session.
	 * Useful for lifecycle management.
	 */
	start: S;

	/** If true, the session's contexts will be included in all events. */
	influenced: boolean;

	createdAt: number;
}

const globalSessions = createSafeGlobalState(() => ({} as Record<symbol, Session>));

/** Get the Snowplow contexts in active influenced sessions if any. */
export function getInfluencedSessionsContexts() {
	const sessions = globalSessions();

	return Object.getOwnPropertySymbols(sessions)
		.map(key => sessions[key])
		.filter(session => session.influenced)
		.map(session => session.data)
		.flat()
		.filter(data => data instanceof SnowplowContext);
}

export function createSessionManager<T, S = any>({ id, influenced, options }: SessionTrackerOptions<Session<T, S>>) {
	const sessions = globalSessions();

	function attachSession(start: S, data: T) {
		sessions[id] = { data, start, createdAt: Date.now(), influenced };
	}

	function killSession() {
		const prevSession = session();
		delete sessions[id];

		return prevSession;
	}

	function session(): Session<T> | null {
		return sessions[id] ?? null;
	}

	let intervalId: NodeJS.Timeout | null = null;

	function startInterval() {
		// already running, no-op
		if (intervalId != null) return;

		intervalId = setInterval(() => {
			const currentSession = session();
			if (currentSession == null) return;

			const now = Date.now();
			if (now - currentSession.createdAt > (options?.sessionTimeoutMs ?? SESSION_TIMEOUT_MS)) {
				const previousSession = killSession();
				options?.onSessionTimeout?.(previousSession);
			}
		}, options?.checkIntervalMs ?? CHECK_INTERVAL_MS);
	}

	function stopInterval() {
		if (intervalId == null) return;
		clearInterval(intervalId);
		intervalId = null;
	}

	return {
		session,
		attachSession,
		killSession,
		startInterval,
		stopInterval,
	};
}
